import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './useAuth';
import { saisonService, SaisonStatus } from '../services/saisonService';
import { dismissSaison, isSaisonDismissed, SAISON_COPY, SaisonCopy } from '../utils/saison';

interface SaisonContextValue {
  copy: SaisonCopy | null;
  dismiss: () => void;
}

const SaisonContext = createContext<SaisonContextValue>({
  copy: null,
  dismiss: () => {},
});

export const useSaison = () => useContext(SaisonContext);

export const SaisonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<SaisonStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    saisonService
      .getStatus({ quiet: true })
      .then((next) => {
        if (!cancelled) setStatus(next);
      })
      .catch(() => {
        // Hinweis ist optional.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user || !status?.instanz) {
      setDismissed(false);
      return;
    }
    setDismissed(isSaisonDismissed(user.id, status.instanz));
  }, [user, status]);

  const dismiss = useCallback(() => {
    if (!user || !status?.instanz) return;
    dismissSaison(user.id, status.instanz);
    setDismissed(true);
  }, [user, status]);

  const copy = status?.aktiv && status.saison && !dismissed ? SAISON_COPY[status.saison] ?? null : null;

  const value = useMemo(() => ({ copy, dismiss }), [copy, dismiss]);

  return <SaisonContext.Provider value={value}>{children}</SaisonContext.Provider>;
};
